import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { OrderList } from "primereact/orderlist";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Message } from "primereact/message";
import { Divider } from "primereact/divider";

import Helpers from "../../utils/helpers";
import {
    calculateItemTotal,
    calculateTotalTax,
} from "../products/product-calculation";
import { itemCount } from "../../redux/slices/counter";
import { isAdmin } from "../../redux/slices/is-admin";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import AuthService from "../../services/autentication/auth.service";
import AppLoading from "../../components/app-loading/app-loading";
import OrderService from "../../services/order/order.service";
import TokenService from "../../services/autentication/token.service";

import { ProductCheckoutModel } from "../../models/products/product-checkout-model";
import { ProductCheckoutProceedModel } from "../../models/products/product-checkout-proceed-model";
import { UserAddress } from "../../models/user-address-model";

const ItemDeleteDialog = lazy(() => import("../basket/item-delete-dialog"));

const ShoppingBagIcon = ({ size = 18 }: { size?: number }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
        <path d="M3 6h18" />
        <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
);

interface SidecartItemsProps {
    mode?: "PAGE" | "SIDECART";
    onProceed?: () => void;
    onClose?: () => void;
}

const SidecartItems: React.FC<SidecartItemsProps> = ({ mode = "PAGE", onProceed, onClose }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const toast = useRef<Toast>(null);
    const count = useAppSelector((state) => state.counter);
    const isAdminUser = useAppSelector((state) => state.isAdmin);
    const adminUsersId = useAppSelector((state) => state.adminUser.selectedAdminUserId);

    const [grandTotal, setGrandTotal] = useState(0);
    const [totalTax, setTotalTax] = useState(0);
    const [productCheckoutToLocal, setProductCheckoutToLocal] = useState<ProductCheckoutModel[]>([]);
    const [isNotifyVisible, setIsNotifyVisible] = useState(false);
    const [checkoutItem, setCheckoutItem] = useState<ProductCheckoutModel>();
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [editingItemId, setEditingItemId] = useState<number | null>(null);
    const [editingValueStr, setEditingValueStr] = useState<string>("");

    // Reward points
    const [availablePoints, setAvailablePoints] = useState<number>(0);
    const [rewardToCurrencyRatio, setRewardToCurrencyRatio] = useState<number>(0);
    const [loadingRewards, setLoadingRewards] = useState<boolean>(false);

    useEffect(() => {
        const isAdminData = ProductStorageHelpers.IsAdminUser();
        dispatch(isAdmin(isAdminData));

        const user = JSON.parse(localStorage.getItem("user") || "null");
        setCurrentUser(user);

        const loadCart = () => {
            const data = ProductStorageHelpers.GetProductStorage();
            const safeData = data && Array.isArray(data) ? data : [];
            setProductCheckoutToLocal(safeData);
            dispatch(itemCount(safeData.length));
        };

        loadCart();
        window.addEventListener("cartUpdated", loadCart);
        return () => window.removeEventListener("cartUpdated", loadCart);
    }, [dispatch]);

    // Fetch reward points
    useEffect(() => {
        const fetchRewards = async () => {
            try {
                setLoadingRewards(true);
                let userId: number | null = null;
                if (isAdminUser && adminUsersId !== 0) {
                    userId = adminUsersId;
                } else if (AuthService.isUserLoggedIn()) {
                    const userDetails = TokenService.getUser();
                    userId = userDetails?.userID || null;
                }
                if (userId) {
                    const response = await OrderService.getUserRewardBalance(userId);
                    setAvailablePoints(response?.availablePoints || 0);
                    setRewardToCurrencyRatio(response?.rewardToCurrencyRatio || 0);
                }
            } catch {
                setAvailablePoints(0);
                setRewardToCurrencyRatio(0);
            } finally {
                setLoadingRewards(false);
            }
        };
        fetchRewards();
    }, [isAdminUser, adminUsersId]);

    useEffect(() => {
        const total = productCheckoutToLocal.reduce((acc, item) => acc + item.quantity * (item.unitPrice || 0), 0);
        const tax = productCheckoutToLocal.reduce((acc, item) => acc + item.quantity * (item.unitPrice || 0) * (item.tax / 100), 0);

        setGrandTotal(total);
        setTotalTax(tax);

        const weight = productCheckoutToLocal.reduce((total, item) => {
            const weightString = typeof item.weight === "string" ? item.weight : "0";
            const numericWeight = parseFloat(weightString.split(" ")[0]);
            return total + (isNaN(numericWeight) ? 0 : numericWeight) * item.quantity;
        }, 0);
        localStorage.setItem("total-weight", JSON.stringify(weight));
    }, [productCheckoutToLocal]);

    const isMixedCart = React.useMemo(() => {
        if (productCheckoutToLocal.length === 0) return false;
        const allSupportCourier = productCheckoutToLocal.every(item => item.courierAvailable === true);
        const allSupportHomeDelivery = productCheckoutToLocal.every(item => item.isHomeDeliveryAvailable === true);
        return !allSupportCourier && !allSupportHomeDelivery;
    }, [productCheckoutToLocal]);

    const hasInvalidQuantity = React.useMemo(() => {
        return productCheckoutToLocal.some(item => item.quantity < (item.minQuantity || 1));
    }, [productCheckoutToLocal]);

    const updateQuantity = (id: number, type: 'inc' | 'dec') => {
        const updated = productCheckoutToLocal.map(item => {
            if (item.id === id) {
                const step = item.orderMultiples || 1;
                const minQty = item.minQuantity || 1;
                if (type === 'inc') {
                    const newQty = parseFloat((item.quantity + step).toFixed(2));
                    return { ...item, quantity: newQty };
                } else {
                    if (item.quantity <= minQty) {
                        return item;
                    }
                    let newQty = parseFloat((item.quantity - step).toFixed(2));
                    if (newQty < minQty) {
                        newQty = minQty;
                    }
                    return { ...item, quantity: newQty };
                }
            }
            return item;
        });
        setProductCheckoutToLocal(updated);
        ProductStorageHelpers.SetProductStorage(updated);
        window.dispatchEvent(new Event("cartUpdated"));
    };

    const handleQuantityInputChange = (id: number, valueStr: string) => {
        if (valueStr !== "" && !/^\d*\.?\d*$/.test(valueStr)) {
            return;
        }

        setEditingItemId(id);
        setEditingValueStr(valueStr);

        const updated = productCheckoutToLocal.map(item => {
            if (item.id === id) {
                let numericVal = valueStr === "" || valueStr === "." ? 0 : parseFloat(valueStr);
                if (isNaN(numericVal)) numericVal = 0;
                return { ...item, quantity: numericVal };
            }
            return item;
        });
        setProductCheckoutToLocal(updated);
        ProductStorageHelpers.SetProductStorage(updated);
        window.dispatchEvent(new Event("cartUpdated"));
    };

    const handleQuantityInputBlur = (id: number, valueStr: string, minQty: number = 1, step: number = 1) => {
        let val = parseFloat(valueStr);
        if (isNaN(val) || val <= 0) {
            val = 0;
        }

        if (val < minQty) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Validation Warning',
                detail: 'Entered quantity is less than minimum quantity',
                life: 3000
            });
            val = minQty;
        } else {
            const steps = Math.round((val - minQty) / step);
            val = minQty + Math.max(0, steps) * step;

            // Avoid floating-point precision issues (e.g. 2.4 -> 2, 2.6 -> 3)
            const getDecimalPlaces = (num: number): number => {
                const match = ('' + num).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
                if (!match) return 0;
                return Math.max(0, (match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0));
            };
            const decimals = Math.max(getDecimalPlaces(minQty), getDecimalPlaces(step));
            val = parseFloat(val.toFixed(decimals));
        }

        setEditingItemId(null);
        setEditingValueStr("");

        const updated = productCheckoutToLocal.map(item => {
            if (item.id === id) {
                return { ...item, quantity: val };
            }
            return item;
        });
        setProductCheckoutToLocal(updated);
        ProductStorageHelpers.SetProductStorage(updated);
        window.dispatchEvent(new Event("cartUpdated"));
    };

    const handleDelete = (item: ProductCheckoutModel) => {
        setCheckoutItem(item);
        setIsNotifyVisible(true);
    };

    const confirmDelete = (value: boolean) => {
        if (value && checkoutItem) {
            const updated = productCheckoutToLocal.filter(p => p.id !== checkoutItem.id);
            setProductCheckoutToLocal(updated);
            ProductStorageHelpers.SetProductStorage(updated);
            dispatch(itemCount(updated.length));
            window.dispatchEvent(new Event("cartUpdated"));
        }
        setIsNotifyVisible(false);
    };

    const checkInvalidProducts = () => {
        const hasCourierUnavailableWithoutHomeDelivery = productCheckoutToLocal.some(
            (item) => item.courierAvailable === false && item.isHomeDeliveryAvailable === false
        );
        return hasCourierUnavailableWithoutHomeDelivery;
    };

    const handleProceed = () => {
        if (checkInvalidProducts()) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Invalid product combination', life: 3000 });
            return;
        }

        if (isMixedCart) {
            toast.current?.show({
                severity: 'error',
                summary: 'Mixed Cart',
                detail: 'Combination of shippable and non-shippable products cannot be delivered together.',
                life: 4000
            });
            return;
        }

        const effectiveUserId = adminUsersId !== 0 ? adminUsersId : (currentUser?.userID || currentUser?.id || 0);

        if (isAdminUser && effectiveUserId === 0) {
            toast.current?.show({ severity: 'info', summary: 'Admin', detail: 'Select a user first', life: 3000 });
            return;
        }

        // Save processed data
        const data: ProductCheckoutProceedModel = {
            id: Date.now().toString(),
            userId: effectiveUserId,
            totalItemCost: grandTotal,
            totalTax: totalTax,
            deliveryHandlingCharges: 0,
            grandItemTotal: Math.round(grandTotal + totalTax),
            items: productCheckoutToLocal,
            address: {} as any,
            paymentOption: { id: "1", mode: "", code: "", status: "Pending", description: "", dateTime: "" },
            comment: "",
            courierProviderID: 0,
            courierCharges: 0,
            packingCharges: 0,
            handlingCharges: 0,
            paymentGatewayCharges: 0,
        };
        ProductStorageHelpers.SetProcessedProductStorage(data);
        onProceed?.();
    };

    const itemTemplate = (item: ProductCheckoutModel) => (
        <div className="product-item">
            <div className="cart-row">
                <div className="image-wrapper">
                    <img className="cart-product-image" src={(item.productImageList && item.productImageList.mediaUrl) ? item.productImageList.mediaUrl : (process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png")} alt={item.name} />
                </div>
                <div className="product-info">
                    <div className="product-name" title={item.name}>{item.name}</div>
                    <div className="product-price">
                        {Helpers.formatAmountInINR(item.unitPrice * item.quantity)}
                    </div>
                    <div className="qty-wrapper">
                        <button className="qty-btn" disabled={item.quantity <= (item.minQuantity || 1)} onClick={() => updateQuantity(item.id, 'dec')}>
                            <i className="pi pi-minus" style={{ fontSize: '0.7rem' }} />
                        </button>
                        <input
                            type="text"
                            inputMode="decimal"
                            className="qty-value-input"
                            value={editingItemId === item.id ? editingValueStr : (item.quantity === 0 ? "" : item.quantity.toString())}
                            onChange={(e) => handleQuantityInputChange(item.id, e.target.value)}
                            onFocus={() => {
                                setEditingItemId(item.id);
                                setEditingValueStr(item.quantity === 0 ? "" : item.quantity.toString());
                            }}
                            onBlur={(e) => handleQuantityInputBlur(item.id, e.target.value, item.minQuantity || 1, item.orderMultiples || 1)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    (e.target as HTMLInputElement).blur();
                                }
                            }}
                            style={{
                                width: '45px',
                                border: 'none',
                                background: 'transparent',
                                textAlign: 'center',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                color: '#1e293b',
                                outline: 'none',
                                padding: 0,
                                margin: 0
                            }}
                        />
                        <button className="qty-btn" onClick={() => updateQuantity(item.id, 'inc')}>
                            <i className="pi pi-plus" style={{ fontSize: '0.7rem' }} />
                        </button>
                    </div>
                </div>
                <div className="cart-delete">
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger" onClick={() => handleDelete(item)} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex flex-column h-full">
            <Toast ref={toast} />
            <div className="side-cart-header">
                <h5><ShoppingBagIcon size={24} /> {t('Your Cart')} ({count})</h5>
                <div className="close-icon" onClick={onClose}>
                    <i className="pi pi-times" />
                </div>
            </div>



            <div className="side-cart-content">
                {productCheckoutToLocal.length > 0 ? (
                    <div className="cart-items-list">
                        {productCheckoutToLocal.map(item => itemTemplate(item))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-400">
                        <i className="pi pi-shopping-bag text-6xl mb-4" />
                        <p className="text-lg font-semibold">{t('Your cart is empty')}</p>
                        <Button label="Continue Shopping" link onClick={onClose} className="mt-2 empty-cart-btn" />
                    </div>
                )}
            </div>

            {productCheckoutToLocal.length > 0 && (
                <div className="cart-summary-footer">
                    <div className="summary-row">
                        <span>Subtotal</span>
                        <span className="font-bold">{Helpers.formatAmountInINR(grandTotal)}</span>
                    </div>
                    <div className="summary-row">
                        <span>Tax</span>
                        <span className="font-bold">{Helpers.formatAmountInINR(totalTax)}</span>
                    </div>
                    <div className="summary-total">
                        <span>Total</span>
                        <span className="amount">{Helpers.formatAmountInINR(Math.round(grandTotal + totalTax))}</span>
                    </div>

                    {/* Rewards Preview */}
                    {!loadingRewards && availablePoints > 0 && (() => {
                        const cartTotal = Math.round(grandTotal + totalTax);
                        const rewardsValue = availablePoints * rewardToCurrencyRatio;
                        const applicableRewards = Math.min(rewardsValue, cartTotal);
                        const finalTotal = Math.max(0, cartTotal - applicableRewards);
                        const pointsToUse = Math.ceil(applicableRewards / (rewardToCurrencyRatio > 0 ? rewardToCurrencyRatio : 1));
                        return (
                            <div style={{ marginTop: '0.5rem' }}>
                                <div className="flex justify-content-between align-items-center">
                                    <span style={{ color: '#667eea', fontSize: '0.82rem', fontWeight: 600 }}>
                                        <i className="pi pi-star-fill mr-1" style={{ fontSize: '0.75rem' }} />
                                        Use <strong style={{ color: '#333' }}>{pointsToUse}</strong> Reward Points
                                    </span>
                                </div>
                                <div className="flex justify-content-between align-items-center mt-1" style={{ borderTop: '2px dashed #e1e8ed', paddingTop: '0.4rem' }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>Pay Just</span>
                                    <div className="flex align-items-center gap-2">
                                        {finalTotal !== cartTotal && (
                                            <span style={{ textDecoration: 'line-through', fontSize: '0.78rem', color: '#999' }}>
                                                {Helpers.formatAmountInINR(cartTotal)}
                                            </span>
                                        )}
                                        <span style={{ color: '#48bb78', fontWeight: 700, fontSize: '1rem' }}>
                                            {Helpers.formatAmountInINR(finalTotal)}
                                        </span>
                                    </div>
                                </div>
                                <p style={{ color: '#677688', fontStyle: 'italic', fontSize: '0.72rem', textAlign: 'center', margin: '0.3rem 0 0' }}>
                                    <i className="pi pi-check-circle mr-1" style={{ fontSize: '0.65rem' }} />
                                    Applied automatically at checkout
                                </p>
                            </div>
                        );
                    })()}

                    {isMixedCart && (
                        <div style={{ backgroundColor: '#ffcdd2', color: '#c62828', padding: '0.8rem', borderRadius: '12px', marginBottom: '1rem', fontSize: '0.85rem', display: 'flex', alignItems: 'start', border: '1px solid #ef9a9a' }}>
                            <i className="pi pi-times-circle mr-2" style={{ fontSize: '1.1rem', marginTop: '2px' }} />
                            <span>Combination of shippable and non-shippable products cannot be delivered together. Please create separate orders.</span>
                        </div>
                    )}

                    <div className="cart-summary-actions">
                        <Button label="Clear" icon="pi pi-refresh" outlined severity="secondary" className="p-button-lg" style={{ borderRadius: '12px' }} onClick={() => { ProductStorageHelpers.SetProductStorage([]); window.dispatchEvent(new Event("cartUpdated")); }} />
                        <Button
                            label="Checkout"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            className="checkout-btn p-button-lg"
                            onClick={handleProceed}
                            disabled={isMixedCart || hasInvalidQuantity}
                        />
                    </div>
                </div>
            )}

            <Suspense fallback={<AppLoading />}>
                <ItemDeleteDialog
                    isVisible={isNotifyVisible}
                    isHide={confirmDelete}
                    messageHeader="Delete Item"
                    notifyMessage="Remove this item from your cart?"
                />
            </Suspense>
        </div>
    );
};

export default SidecartItems;
